import {
  all,
  call,
  cancel,
  delay,
  cancelled as loginCancelled,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';

import MobileDeviceHelpers from '../../utils/MobileDeviceHelpers';
import Biometrics from '../../utils/Biometrics';
import { logActionGA } from '../../utils/googleAnalyticsLogging';
import KeychainStore from '../../utils/KeychainStore';
import {
  CHECKMARK_TIME_UNTIL_TRANSITION,
  DEFAULT_ERROR_MSG,
  GENERAL_SERVER_ERROR_MSG,
  LANGUAGE_ENGLISH,
  LANGUAGE_ENGLISH_MURABAHA,
  SELECTED_LOAN_SESSION_KEY,
} from '../../utils/constants';
import { get, post, putRequest } from '../../utils/request';
import {
  deleteEncryptedClients,
  generateCodeChallenge,
  generateCodeVerifier,
  generateQueryString,
  getCookie,
  getEncryptedClients,
  getQueryParam,
  setCookie,
  setEncryptedClients,
} from '../../utils/globalHelpers';
import {
  clearSession,
  getAuthProp,
  getAuthStatus,
  retrieveSession,
  setSession,
} from '../../utils/sessionHelpers';
import AsyncStorage from '../../utils/AsyncStorage';
import {
  goldMigrationLoginRequestFailure,
  goldMigrationLoginRequestSuccess,
  setGoldMigrationCookies,
  updateGoldMigrationSession,
  updateIsUsernameRequired,
  updateProfileTransferStep,
} from '../GoldRedirect/actions';
import {
  API_REASON_CODE_DICTIONARY,
  GOLD_MIGRATION_LOGIN_REQUEST,
  REDIRECT_TO_PAYMENTS_PAGE,
} from '../GoldRedirect/constants';
import {
  focusInput,
  toggleDrawer,
  updateMfaOptions,
} from '../Login/actions';
import { SSO_LOGIN_ERROR_MESSAGE } from '../Login/constants';

import makeSelectMobile from '../Mobile/selectors';
import { setNativeClientList } from '../Mobile/actions';
import { updatePaymentHomeInit } from '../Payments/OneTimePayment/actions';
import { setPreAuthNotifications } from '../Notifications/actions';
import {
  ACCOUNT_LOCKED_BIOMETRIC_ERROR_MSG,
  ACCOUNT_LOCKED_PIN_ERROR_MSG,
  BIOMETRICS_API_ERROR_MSG,
  BIOMETRICS_DISABLED_ERROR_MSG,
  PIN_LOCKED_ERROR_MSG,
} from '../Settings/Security/constants';
import { disableAltAuthMethods, updateBiometricsAndPinInfo } from '../Settings/Security/actions';
import { setPreAuthFeeShedules } from '../Fees/actions';
import { changeStep, updateRegisterCurrentEmail } from '../Register/Main/actions';
import { REGISTER_STEP_ONE_ERROR } from '../Register/Main/constants';
import {
  biometricLoginRequestFailure,
  cancelBiometricAuthentication,
  cancelChangeSelectedLoanSuccess,
  changeSelectedLoanFailure,
  changeSelectedLoanSuccess,
  closeModal,
  disableInstantPostLoginModal,
  fetchOidcConfigFailure,
  fetchRespaLetterFailure,
  fetchTransferStatusFailure,
  fetchTransferStatusSuccess,
  initializeRequestFailure,
  initializeRequestSuccess,
  // initializeOidcLogin,
  linkNativeClientFailure,
  linkNativeClientSuccess,
  loginRequest,
  loginRequestCancelled,
  loginRequestFailure,
  loginRequestSuccess,
  logoutRequestSuccess,
  mfaOptionRequestFailure,
  mfaOptionRequestSuccess,
  nativeClientsRequestFailure,
  // oauthLogin,
  oauthLoginFailure,
  oauthLoginSuccess,
  openPinDrawerFailure,
  pinLoginRequestFailure,
  putLanguagePreferenceFailure,
  putLanguagePreferenceSuccess,
  putPaperlessSettingFailure,
  putPaperlessSettingSuccess,
  reconsentRequestFailure,
  reconsentRequestSuccess,
  refreshMonitor,
  refreshToken as refreshTokenAction,
  refreshTokenFailure,
  resetSwitchLoanState,
  resetUserState,
  setChatbotAuthToken,
  setIsChangeSelectedLoanInProgress,
  setIsSslAuth,
  setRespaFile,
  setSelectedLanguage,
  setSsoMigration,
  totpRequestFailure,
  totpRequestSuccess,
  updateDismissedBanners,
  updateDismissedDocUploadStatusBanners,
  updateIsLoggedIn,
  updateRefreshToken,
  updateSsoMigrationUrl,
  updateUserLoansData,
} from './actions';
import {
  AUTH_FULL,
  BIOMETRIC_LOGIN_REQUEST,
  CANCEL_CHANGE_SELECTED_LOAN,
  CANCEL_LOGIN,
  CHANGE_SELECTED_LOAN,
  CHANGE_SELECTED_LOAN_ERROR_MSG,
  ERROR_MESSAGES,
  FETCH_OIDC_CONFIG,
  FETCH_RESPA_LETTER,
  FETCH_RESPA_LETTER_FAILURE_MSG,
  FETCH_TRANSFER_STATUS,
  INITIALIZE_REQUEST,
  INVALID_BIOMETRICS_ENROLLMENT_KEYCHAIN,
  INVALID_PIN_KEYCHAIN,
  INVALID_REFRESH_TOKEN_KEYCHAIN,
  INVALID_USERNAME_KEYCHAIN,
  LETTER_BLOCKED_MESSAGE,
  LINK_CLIENT_LOCKOUT_MESSAGE,
  LINK_NATIVE_CLIENT,
  LOGIN_REQUEST,
  LOGOUT_INACTIVITY_MESSAGE,
  LOGOUT_REQUEST,
  LOGOUT_SESSION_EXPIRATION_MESSAGE,
  LOGOUT_SUCCESS_MESSAGE,
  MFA_OPTION_REQUEST,
  MFA_OPTION_REQUEST_CANCEL,
  NATIVE_CLIENT_ERROR_MESSAGE,
  NATIVE_CLIENT_RESET_ERROR_MESSAGE,
  OAUTH_LOGIN,
  OPEN_PIN_DRAWER,
  PIN_LOGIN_REQUEST,
  PUT_LANGUAGE_PREFERENCE,
  PUT_PAPERLESS_SETTING,
  RECONSENT_REQUEST,
  REDIRECT_NAV_DICT,
  REFRESH_MONITOR,
  REFRESH_TOKEN,
  SET_LANGUAGE_PICKER,
  STORAGE_KEY_LANGUAGE_PICKER_VALUE,
  STORAGE_KEY_LOSS_DRAFT_READ_CLAIM_NUMBERS,
  STORAGE_KEY_NOTIFICATIONS_MODAL_AUTO_OPENED,
  TOKEN_EXPIRATION_ERROR_MSG,
  TOTP_CANCEL_REQUEST,
  TOTP_REQUEST,
  TRANSFER_STATUS_UNAVAILABLE,
} from './constants';
import {
  checkIfIsNewUser,
  getDismissedDocUploadStatusBanners,
  getDismissedPreLoginBanners,
  getIsLockedOut,
  getNavHandler,
  handleAccountLockout,
  handleClientLinkFailure,
  handlePinFailure,
  removeClientLinkAttemptCount,
  removeItemsFromAsyncStorage,
  removeLockoutExpirationTime,
  removePinAttemptCount,
} from './helpers';
import makeSelectMain, {
  getIsSso,
  getMainClientAuthProps,
  getProfileCurrentSetting,
  getSsoType,
  getWebSsoLogoutType,
} from './selectors';

export function* logActionSaga(action) {
  logActionGA(action);
}

// eslint-disable-next-line no-unused-vars
export function* biometricLoginRequestSaga({ payload: { ssoClientDetails } }) {
  try {
    const isAccountLockedOut = yield call(getIsLockedOut, 'account');
    if (isAccountLockedOut) {
      throw new Error(ACCOUNT_LOCKED_BIOMETRIC_ERROR_MSG);
    }
    const {
      hasBiometrics,
      refreshTokenKeychain,
      usernameKeychain,
    } = yield call(KeychainStore.getAll);
    // This would indicate biometric enrollment key corruption, or an error fetching it.
    if (hasBiometrics === INVALID_BIOMETRICS_ENROLLMENT_KEYCHAIN) {
      throw new Error(TOKEN_EXPIRATION_ERROR_MSG);
    }
    if (hasBiometrics) {
      const { error, success } = yield call(Biometrics.authenticate);
      if (error === 'user_cancel') {
        yield put(cancelBiometricAuthentication());
        return;
      }
      if (!success) {
        throw new Error(BIOMETRICS_API_ERROR_MSG);
      }
      // if (!isEmpty(ssoClientDetails)) {
      //   const encryptedClients = yield call(getEncryptedClients);
      //   yield put(oauthLogin({ ...ssoClientDetails, encryptedClients }));
      // } else {
      if (usernameKeychain === INVALID_USERNAME_KEYCHAIN ||
          refreshTokenKeychain === INVALID_REFRESH_TOKEN_KEYCHAIN) {
        /**
         * This would indicate biometrics is enabled but either there was a problem retrieving
         * the username or refresh token, or they've been corrupted. In this case, we want to
         * display a banner indicating to login with username and password to reset the secure store
         * with fresh credentials.
         */
        throw new Error(TOKEN_EXPIRATION_ERROR_MSG);
      }
      yield put(loginRequest({
        refreshToken: refreshTokenKeychain,
        username: usernameKeychain,
      }));
      // }
    } else {
      throw new Error(BIOMETRICS_DISABLED_ERROR_MSG);
    }
  } catch ({ message }) {
    const manuallyThrownErrors = [
      ACCOUNT_LOCKED_BIOMETRIC_ERROR_MSG,
      BIOMETRICS_API_ERROR_MSG,
      BIOMETRICS_DISABLED_ERROR_MSG,
      TOKEN_EXPIRATION_ERROR_MSG,
    ];
    const errorMessage =
      manuallyThrownErrors.includes(message) ? message : DEFAULT_ERROR_MSG;
    yield put(biometricLoginRequestFailure(errorMessage));
  }
}

function* changeSelectedLoanSaga({ baseUrl, navigationHandler }, { payload }) {
  const isMobile = yield select(makeSelectMobile('isMobile'));
  const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
  const { authenticatedBy } = yield select(getProfileCurrentSetting());
  const { showLanguagePicker } = yield select(makeSelectMain('testFlags'));
  try {
    yield put(setIsChangeSelectedLoanInProgress(true));
    if (isMobile) {
      navigationHandler('Loading');
    }
    const [requestData, cancelled] = yield race([
      call(post, `${baseUrl}/loans/selected`, { authenticatedBy, ...payload }),
      take(CANCEL_CHANGE_SELECTED_LOAN),
    ]);
    if (cancelled) {
      // This can only apply for desktop. Mobile-web and native build
      // switch loans request cannot be cancelled.
      const selectedLoan = yield select(makeSelectMain('selectedLoan'));
      yield call(setSession, { [SELECTED_LOAN_SESSION_KEY]: selectedLoan });
      yield put(cancelChangeSelectedLoanSuccess());
      return;
    }
    const { loanDetails: { summary: { isMurabaha } } } = requestData;
    // When language picker is enabled, override account language unless set to 'default' option
    const accountLanguage = isMurabaha ? LANGUAGE_ENGLISH_MURABAHA : LANGUAGE_ENGLISH;
    if (showLanguagePicker) {
      const pickerLanguage = yield call(AsyncStorage.getItem, STORAGE_KEY_LANGUAGE_PICKER_VALUE);
      yield put(setSelectedLanguage(pickerLanguage || accountLanguage));
    } else {
      yield put(setSelectedLanguage(accountLanguage));
    }
    yield put(changeSelectedLoanSuccess(requestData));
    if (!isMobile) {
      yield put(resetSwitchLoanState());
      yield put(closeModal({ modalState: 'isSwitchLoanModalOpen' }));
      // allow time for modal to fully close
      yield delay(1000);
    }

    const path = isMobile ? 'Payments' : '/payments';
    if (isNativeMobile) {
      // Checking native v mobile web in the case of switching
      // from an inactive loan (drawer navigator) -> active loan (nested tab navigator)
      // should have no ill effect when switching from active -> active
      navigationHandler('TabNavigator', { component: path });
    } else {
      // A delay was needed for the inactive-to-active loan redirect to work consistently
      // to introduce a break in synchronous execution and ensure a smooth redirect
      // to the payments page
      yield delay(200);
      navigationHandler(path);
    }
  } catch (error) {
    if (isMobile) {
      navigationHandler('SelectLoan');
    }
    yield put(changeSelectedLoanFailure(CHANGE_SELECTED_LOAN_ERROR_MSG));
  } finally {
    yield put(setIsChangeSelectedLoanInProgress(false));
  }
}

export function* initializeRequestSaga({ baseUrl, navigationObj }) {
  const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
  // Only check for cookies on web
  if (!isNativeMobile) {
    const goldMigrationPLGeneralLogin = getCookie('goldMigrationPLGeneralLoginTokenId');
    const goldMigrationPLPreLogin = getCookie('goldMigrationPLPreLogin');
    const goldMigrationPLProfileTransfer = getCookie('goldMigrationPLProfileTransferTokenId');
    yield put(setGoldMigrationCookies({
      goldMigrationPLGeneralLogin,
      goldMigrationPLPreLogin,
      goldMigrationPLProfileTransfer,
    }));
  }
  try {
    const minimumSplashScreenTime = isNativeMobile ? 2000 : 0;

    const [requestData] = yield all([
      call(get, `${baseUrl}/login/init`, {}, {
        sendAnonymousSplitId: true,
        sendDeviceTokens: true,
      }),
      // call(fetchNativeClientsSaga, { baseUrl }),
      delay(minimumSplashScreenTime),
    ]);
    const sessionState = yield call(getAuthStatus);

    yield call(postAuthRequestSaga, {
      baseUrl,
      isInitRequest: true,
      navigationObj,
      requestData,
      sessionState,
      successAction: initializeRequestSuccess,
    });
  } catch (error) {
    const { message, statusCode } = error;
    if (statusCode === 401) {
      if (process.env.APP === 'csr') {
        // Send a message to CSR for re-authentication
        window.parent.postMessage({ msg: 'borrower-view-auth-error' }, '*');
      } else {
        yield call(initializeRequestSaga, { baseUrl, navigationObj });
      }
    } else if (message !== 'nativeClientsFetchError') {
      yield put(initializeRequestFailure(DEFAULT_ERROR_MSG));
      const isMobile = yield select(makeSelectMobile('isMobile'));
      if (!isMobile) {
        const navigationHandler = getNavHandler({ navigationObj });
        navigationHandler('/login');
      }
    }
  }
}

/* eslint-disable camelcase */
export function* fetchOidcConfigSaga({ payload }) {
  try {
    const {
      clientId,
      discoveryUrl,
      oidcAuthorizationExtraQueryParams,
      oidcAuthorizationScope,
    } = payload;
    const { authorization_endpoint } = yield call(
      get,
      discoveryUrl,
      {},
      { withSession: false },
      { excludeCommonHeaders: true },
    );
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = yield generateCodeChallenge(codeVerifier);

    localStorage.setItem('codeVerifier', codeVerifier);
    const redirectUri = `${window.location.origin}/oauthcallback`;
    const queryParams = {
      client_id: clientId,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: oidcAuthorizationScope,
      ...oidcAuthorizationExtraQueryParams,
    };
    const queryString = generateQueryString(queryParams);
    const fullRedirectUri = `${authorization_endpoint}?${queryString}`;
    window.location.href = fullRedirectUri;
  } catch (error) {
    yield put(fetchOidcConfigFailure(SSO_LOGIN_ERROR_MESSAGE));
  }
}
/* eslint-enable camelcase */

/**
 * @description: Makes request to proxy server to fetch RESPA letter
 * data
 */
export function* fetchRespaLetterSaga({ baseUrl }, { payload }) {
  const { dispatchSetFile, dispatchToggleDrawer, statementId } = payload;
  try {
    const loanNumber = yield select(makeSelectMain('selectedLoan'));
    const endpoint =
      `${baseUrl}/documents/statements/${statementId}?loanNumber=${loanNumber}`;
    const { error, fileContent } = yield call(get, endpoint);
    if (error) {
      yield put(fetchRespaLetterFailure(FETCH_RESPA_LETTER_FAILURE_MSG));
    } else {
      const fileName = `respa_letter_${statementId}.pdf`;
      const isMobile = yield select(makeSelectMobile('isMobile'));
      if (isMobile) {
        const errorMessage = yield call(MobileDeviceHelpers.saveAttachmentToFile, {
          dispatchSetFile,
          dispatchToggleDrawer,
          fileContent,
          fileName,
        });
        if (errorMessage) {
          yield put(fetchRespaLetterFailure(LETTER_BLOCKED_MESSAGE));
        }
      } else {
        yield put(setRespaFile({ fileName, uri: fileContent }));
      }
    }
  } catch (err) {
    yield put(fetchRespaLetterFailure(FETCH_RESPA_LETTER_FAILURE_MSG));
  }
}

export function* fetchTransferStatusSaga({ baseUrl }) {
  try {
    const loanNumber = yield select(makeSelectMain('selectedLoan'));
    const endpoint = `${baseUrl}/settings/${loanNumber}/transfer/status`;
    const {
      error,
      servicingTransferDetail,
    } = yield call(get, endpoint);
    if (error) {
      if (error.reasons[0] === '30000.1000') {
        yield put(fetchTransferStatusFailure({ error: TRANSFER_STATUS_UNAVAILABLE }));
      }
    } else {
      yield put(fetchTransferStatusSuccess({ servicingTransferDetail }));
    }
  } catch (error) {
    yield put(fetchTransferStatusFailure({ error: TRANSFER_STATUS_UNAVAILABLE }));
  }
}

export function* logoutRequestSaga(
  { baseUrl, navigationHandler },
  { payload: { isInactive = false, isSessionExpired = false, wasInBackground = false } },
) {
  const isMobile = yield select(makeSelectMobile('isMobile'));
  const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
  const isSso = yield select(getIsSso());
  const webSsoLogoutType = yield select(getWebSsoLogoutType());
  const ssoRedirectUrl = yield select(getMainClientAuthProps('ssoRedirectUrl'));
  const ssoType = yield select(getSsoType());
  try {
    let messageToDisplay = '';
    if (isSessionExpired) {
      messageToDisplay = LOGOUT_SESSION_EXPIRATION_MESSAGE;
    } else if (isInactive) {
      messageToDisplay = LOGOUT_INACTIVITY_MESSAGE;
    } else {
      messageToDisplay = LOGOUT_SUCCESS_MESSAGE;
    }
    const path = REDIRECT_NAV_DICT[isMobile ? 'mobile' : 'desktop'].logout;
    /**
     * Desktop only (for now) - this will need to be relocated when loss draft
     * is implemented on mobile
     */
    if (!isMobile) {
      yield call(removeItemsFromAsyncStorage, [
        STORAGE_KEY_LOSS_DRAFT_READ_CLAIM_NUMBERS,
        STORAGE_KEY_NOTIFICATIONS_MODAL_AUTO_OPENED,
      ]);
    }

    // This isn't really an "error", we're just putting the associated logic in the catch block
    if (isSso && !isNativeMobile && webSsoLogoutType !== 'slo') {
      throw new Error();
    }

    if (isSso && !isNativeMobile) {
      if (ssoType === 'oidc') {
        yield call(post, `${baseUrl}/login/logout`);
        const ssoLogoutUrl = yield select(makeSelectMain('ssoLogoutUrl'));
        window.location.href = ssoLogoutUrl;
      } else {
        const sub = yield call(getAuthProp, 'sub');
        if (!sub) {
          throw new Error();
        }
        const {
          logoutUrl,
          samlLogoutRequest,
        } = yield call(post, `${baseUrl}/login/logout`, { isSso: true, sub });
        if (!logoutUrl || !samlLogoutRequest) {
          const error = new Error('Missing SSO SLO Request');
          throw error;
        }
        // HTTP-POST SAML SLO request
        const form = document.createElement('form');
        const requestInput = document.createElement('input');
        form.method = 'POST';
        form.action = logoutUrl;
        form.encType = 'application/x-www-form-urlencoded';
        requestInput.name = 'SAMLRequest';
        requestInput.type = 'hidden';
        requestInput.value = samlLogoutRequest;
        form.appendChild(requestInput);
        document.body.appendChild(form);
        form.submit();
      }
    } else {
      yield call(clearSession);
      if (!isMobile) {
        yield put(updateIsLoggedIn({ isLoggedIn: false }));
        navigationHandler(path);
        yield put(resetUserState());
        yield put(logoutRequestSuccess({
          message: messageToDisplay,
          shouldBiometricsOpenAutomatically: wasInBackground,
        }));
      } else {
        yield put(updateIsLoggedIn({ isLoggedIn: false }));
        yield put(resetUserState());
        yield put(logoutRequestSuccess({
          message: messageToDisplay,
          shouldBiometricsOpenAutomatically: wasInBackground,
        }));
      }
    }
  } catch (error) {
    yield call(clearSession);
    if (isSso && !isNativeMobile && webSsoLogoutType !== 'generic' && ssoRedirectUrl) {
      /**
       * (1) Initial fallback for sso logout type 'slo', or (2) if we intentionally set client
       * logoutType to be 'redirect'
       * - navigate to client website
       */
      window.location.href = ssoRedirectUrl;
    } else if (isSso && !isNativeMobile) {
      /**
       * (1) General fallback for web sso, or (2) if we intentionally set client logoutType to be
       * 'generic':
       * - navigate to generic non-interactive logged out page on SS3
       */
      yield put(resetUserState());
      yield put(updateIsLoggedIn({ isLoggedIn: false, isLoggedOutViaSsoGeneric: true }));
      if (!isMobile) {
        navigationHandler('/logout/sso');
      }
    } else {
      /**
       * For all non-sso and all native errors
       */
      yield put(resetUserState());
      yield put(updateIsLoggedIn({ isLoggedIn: false }));
      if (!isMobile) {
        navigationHandler('/login');
      }
    }
  }
}

export function* loginSaga({ baseUrl, navigationHandler, navigationObj }, { payload }) {
  if (!payload) {
    yield cancel();
  }
  const isMobile = yield select(makeSelectMobile('isMobile'));
  const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
  try {
    const { password, refreshToken, username } = payload;
    const path = refreshToken ? '/biometric' : '';
    const endpoint = `${baseUrl}/login${path}`;
    const { mfaOptions, ...requestData } = yield call(
      post,
      endpoint,
      { password, refreshToken, username },
      { sendDeviceTokens: true },
    );
    const sessionState = yield call(getAuthStatus);
    if (mfaOptions) {
      yield put(updateMfaOptions({ mfaOptions }));
    }
    yield call(postAuthRequestSaga, {
      baseUrl,
      isLoginRequest: true,
      navigationObj,
      requestData,
      sessionState,
      successAction: loginRequestSuccess,
    });
  } catch (error) {
    const { refreshToken } = payload;
    const { additionalData, statusCode } = error;
    const { errorCode } = additionalData || {};
    if (errorCode === 1014) {
      // User has migrated to SSO
      const { additionalInformation: { migrationUrl } } = additionalData;
      yield put(updateSsoMigrationUrl({ migrationUrl }));
      if (isMobile) {
        navigationHandler('SsoAccountError');
      } else {
        navigationHandler('/login/sso');
      }
    } else if (errorCode === 1015) {
      // User flagged for SSO migration
      const {
        additionalInformation: {
          currentUserEmail,
          migrationImage,
          migrationLoginPage,
          migrationUrl,
        },
      } = additionalData;
      yield put(setSsoMigration({
        currentUserEmail,
        migrationImage,
        migrationLoginPage,
        migrationUrl,
      }));
      if (isMobile && !isNativeMobile) {
        navigationHandler('SsoMigrationModal');
      } else if (isNativeMobile) {
        yield put(loginRequestFailure(DEFAULT_ERROR_MSG));
      }
    } else {
      if (errorCode === 1001) {
        yield call(handleAccountLockout);
      }
      let messageToDisplay = DEFAULT_ERROR_MSG;
      if (statusCode === 400 && refreshToken) {
        messageToDisplay = TOKEN_EXPIRATION_ERROR_MSG;
      } else if (ERROR_MESSAGES[errorCode]) {
        messageToDisplay = ERROR_MESSAGES[errorCode];
      }
      if (errorCode === 1006) {
        const { additionalInformation: { currentUserEmail } } = additionalData;
        // Login attempt before registration verification.
        yield put(changeStep(9));
        yield put(updateRegisterCurrentEmail(currentUserEmail));
        const navHandler = getNavHandler({ isLoginRequest: false, isMobile, navigationObj });
        if (isMobile) {
          yield call(navHandler, 'Register');
        } else {
          yield call(navHandler, '/register');
        }
        return;
      }
      yield put(loginRequestFailure(messageToDisplay));
    }
  } finally {
    if (yield loginCancelled()) {
      yield put(loginRequestCancelled());
    }
  }
}

export function* goldMigrationLoginSaga({ baseUrl, navigationObj }, { payload }) {
  try {
    const {
      operation,
      password,
      username,
      usernameForLogin,
    } = payload;
    const endpoint = `${baseUrl}/login/goldprofiletransfer`;
    const response = yield call(
      post,
      endpoint,
      {
        password,
        username: operation === 'MergeWithSilver' ? usernameForLogin : username,
      },
      { sendDeviceTokens: true },
    );
    const { error, ...requestData } = response;

    if (error) {
      const { reasons } = error;
      if (reasons.includes('3006.2018')) {
        yield put(updateIsUsernameRequired(true));
      }
      if (reasons.includes('3006.2022')) {
        yield put(updateProfileTransferStep(3));
        yield put(goldMigrationLoginRequestSuccess());
      } else if (reasons.includes('3006.2029')) {
        yield put(goldMigrationLoginRequestFailure({ fullError: true }));
        yield put(updateProfileTransferStep(4));
      } else {
        const formErrors = reasons.map((reason) => API_REASON_CODE_DICTIONARY[reason]);
        if (formErrors.includes(undefined)) {
          yield put(goldMigrationLoginRequestFailure({ fullError: true }));
          yield put(updateProfileTransferStep(4));
        } else {
          yield put(goldMigrationLoginRequestFailure({ formErrors }));
        }
      }
    } else {
      const sessionState = yield call(getAuthStatus);

      if (operation !== 'MergeWithSilver') {
        yield put(updateProfileTransferStep(2));
        yield race([
          delay(5000),
          take(REDIRECT_TO_PAYMENTS_PAGE),
        ]);
      }

      yield call(postAuthRequestSaga, {
        baseUrl,
        isLoginRequest: true,
        navigationObj,
        requestData,
        sessionState,
        skipDelayCheckmark: true,
        successAction: goldMigrationLoginRequestSuccess,
      });
    }
  } catch (error) {
    yield put(goldMigrationLoginRequestFailure({ fullError: true }));
    yield put(updateProfileTransferStep(4));
  }
}

export function* mfaSelectionSaga({ baseUrl, navigationObj }, { payload: { mfaOption } }) {
  try {
    const endpoint = `${baseUrl}/login/mfa`;
    const { requestData } = yield race({
      cancel: take(MFA_OPTION_REQUEST_CANCEL),
      requestData: call(post, endpoint, { mfaOption }),
    });
    if (requestData) {
      const sessionState = yield call(getAuthStatus);
      yield call(postAuthRequestSaga, {
        baseUrl,
        navigationObj,
        sessionState,
        successAction: mfaOptionRequestSuccess,
      });
    }
  } catch (error) {
    const messageToDisplay = GENERAL_SERVER_ERROR_MSG;
    yield put(mfaOptionRequestFailure(messageToDisplay));
  }
}

/* eslint-disable camelcase */
export function* oauthLoginSaga({ baseUrl, navigationObj }, { payload }) {
  try {
    const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
    let requestData;
    if (isNativeMobile) {
      const encryptedClients = yield call(getEncryptedClients);
      requestData = yield call(
        post,
        `${baseUrl}/login/oauth/mobile`,
        { ...payload, encryptedClients },
      );
    } else {
      const { code } = payload;
      const codeVerifier = localStorage.getItem('codeVerifier');
      localStorage.removeItem('codeVerifier');
      const clientId = yield select(getMainClientAuthProps('clientId'));
      const clientSecret = yield select(getMainClientAuthProps('clientSecret'));
      const discoveryUrl = yield select(getMainClientAuthProps('discoveryUrl'));
      const redirectUri = `${window.location.origin}/oauthcallback`;
      const postLogoutRedirectUri = `${window.location.origin}/login/oauth`;
      const {
        end_session_endpoint,
        issuer,
        jwks_uri,
        token_endpoint,
        userinfo_endpoint,
      } = yield call(
        get,
        discoveryUrl,
        {},
        { withSession: false },
        { excludeCommonHeaders: true },
      );
      requestData = yield call(
        post,
        `${baseUrl}/login/oauth/desktop`,
        {
          clientId,
          clientSecret,
          code,
          codeVerifier,
          endSessionEndpoint: end_session_endpoint,
          issuer,
          jwksUri: jwks_uri,
          postLogoutRedirectUri,
          redirectUri,
          tokenEndpoint: token_endpoint,
          userInfoEndpoint: userinfo_endpoint,
        },
      );
    }
    const sessionState = yield call(getAuthStatus);
    yield call(postAuthRequestSaga, {
      baseUrl,
      navigationObj,
      requestData,
      sessionState,
      successAction: loginRequestSuccess,
    });
    yield put(oauthLoginSuccess());
  } catch ({ additionalData }) {
    const message = additionalData && additionalData.errorDescription === 'refreshTokenInvalid' ?
      TOKEN_EXPIRATION_ERROR_MSG : SSO_LOGIN_ERROR_MESSAGE;
    yield put(oauthLoginFailure(message));
  }
}
/* eslint-enable camelcase */

export function* openPinDrawerSaga() {
  try {
    const isAccountLockedOut = yield call(getIsLockedOut, 'account');
    const isPinLockedOut = yield call(getIsLockedOut, 'pin');
    const pinKeychain = yield call(KeychainStore.getPin);
    if (isAccountLockedOut) {
      yield put(openPinDrawerFailure({ error: ACCOUNT_LOCKED_PIN_ERROR_MSG }));
    } else if (isPinLockedOut) {
      yield put(openPinDrawerFailure({ error: PIN_LOCKED_ERROR_MSG }));
    } else if (pinKeychain === INVALID_PIN_KEYCHAIN) {
      yield put(openPinDrawerFailure({ error: TOKEN_EXPIRATION_ERROR_MSG }));
    } else {
      yield call(removeLockoutExpirationTime);
      yield put(toggleDrawer(true));
    }
  } catch {
    yield put(openPinDrawerFailure({ error: DEFAULT_ERROR_MSG }));
  }
}

export function* pinLoginRequestSaga({ payload }) {
  // eslint-disable-next-line no-unused-vars
  const { pin, ssoClientDetails } = payload;
  try {
    const sixDigitPinKeychain = yield call(KeychainStore.getPin);
    if (!sixDigitPinKeychain) throw new Error();
    if (pin !== sixDigitPinKeychain) throw Error('pin failure');

    // if (!isEmpty(ssoClientDetails)) {
    //   const encryptedClients = yield call(getEncryptedClients);
    //   yield put(toggleDrawer(false));
    //   yield put(oauthLogin({ ...ssoClientDetails, encryptedClients }));
    // } else {
    const refreshTokenKeychain = yield call(KeychainStore.getRefreshToken);
    const usernameKeychain = yield call(KeychainStore.getUsername);
    yield put(toggleDrawer(false));
    yield put(
      loginRequest({
        refreshToken: refreshTokenKeychain,
        username: usernameKeychain,
      }),
    );
    // }
  } catch ({ message }) {
    if (message === 'pin failure') {
      const { errorMessage, isLockedOut } = yield call(handlePinFailure);
      if (isLockedOut) {
        yield put(toggleDrawer(false));
        yield put(pinLoginRequestFailure({ error: errorMessage, isDrawerError: false }));
      } else {
        yield put(pinLoginRequestFailure({ error: errorMessage, isDrawerError: true }));
        yield put(focusInput({ field: 'pin' }));
      }
    } else {
      yield put(toggleDrawer(false));
      yield put(pinLoginRequestFailure({ error: DEFAULT_ERROR_MSG, isDrawerError: false }));
    }
  }
}

export function* putNYCLanguagePreferenceSaga({ baseUrl }, { payload }) {
  const { languageCode, loanNumber } = payload;
  try {
    const endpoint = `${baseUrl}/settings/${loanNumber}/language`;
    const { error } = yield call(putRequest, endpoint, { languageCode });
    if (error) {
      throw new Error();
    } else {
      yield put(putLanguagePreferenceSuccess());
    }
  } catch {
    yield put(putLanguagePreferenceFailure());
  }
}

export function* putPaperlessSettingSaga({ baseUrl }, { payload }) {
  const { loanNumber, requestedOperation } = payload;
  try {
    const endpoint = `${baseUrl}/settings/${loanNumber}/paperless`;
    const { error } = yield call(putRequest, endpoint, { requestedOperation });
    if (error) {
      throw new Error();
    } else {
      yield put(putPaperlessSettingSuccess());
    }
  } catch {
    yield put(putPaperlessSettingFailure());
  } finally {
    yield put(disableInstantPostLoginModal());
  }
}

export function* reconsentSaga({ baseUrl, navigationObj }, { payload: { consentsAgreed } }) {
  try {
    const endpoint = `${baseUrl}/login/reconsent`;
    const requestData = yield call(post, endpoint, { consentsAgreed }, { sendDeviceTokens: true });
    const sessionState = yield call(getAuthStatus);
    yield call(postAuthRequestSaga, {
      baseUrl,
      navigationObj,
      requestData,
      sessionState,
      successAction: reconsentRequestSuccess,
    });
  } catch (error) {
    const { additionalData } = error;
    const { errorCode } = additionalData || {};
    const messageToDisplay = ERROR_MESSAGES[errorCode] || DEFAULT_ERROR_MSG;
    yield put(reconsentRequestFailure({ error: messageToDisplay }));
  }
}

export function* refreshMonitorSaga() {
  const ssoType = yield select(getSsoType());
  const delayTime = (ssoType === 'samlIDP' || ssoType === 'ssl') ?
    210000 : // 3.5 min in ms
    1500000; // 25 min in ms
  let isLoggedIn = true;
  while (isLoggedIn) {
    const [isStillActive] = yield race([
      delay(delayTime),
      take(LOGOUT_REQUEST),
    ]);
    if (isStillActive) {
      yield put(refreshTokenAction());
    } else {
      isLoggedIn = false;
    }
  }
}

export function* refreshTokenSaga({ baseUrl }) {
  try {
    const { chatbotAuthToken, refreshTokenUpdated } = yield call(post, `${baseUrl}/login/refresh`);
    yield put(setChatbotAuthToken(chatbotAuthToken));
    yield put(updateRefreshToken(refreshTokenUpdated));
  } catch {
    yield put(refreshTokenFailure());
  }
}

export function* refreshPreauthStatementUrlSaga(baseUrl) {
  try {
    const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
    let delayTime = 300000; // 5 min in ms
    const isLoggedIn = yield select(makeSelectMain('isLoggedIn'));
    while (!isLoggedIn) {
      let statementUrlExpiresAt;
      if (!isNativeMobile) {
        statementUrlExpiresAt = getCookie('preauthStatementUrlExpiresAt');
      } else {
        statementUrlExpiresAt = yield call(AsyncStorage.getItem, 'preauthStatementUrlExpiresAt');
      }
      yield delay(delayTime);
      const isStillLoggedIn = yield select(makeSelectMain('isLoggedIn'));
      if (isStillLoggedIn) {
        break;
      }
      const expirationTime = moment(statementUrlExpiresAt).diff(moment());
      // Set delay time to 5 minutes, or less if URL is about to expire
      delayTime = expirationTime > delayTime ? delayTime : expirationTime;
      if (delayTime <= 0) {
        const { feeStatements } = yield call(get, `${baseUrl}/branding/feeschedule`);
        if (!isEmpty(feeStatements)) {
          yield put(setPreAuthFeeShedules({ feeStatements }));
          statementUrlExpiresAt = feeStatements[0].statementUrlExpiresAt;
          if (!isNativeMobile) {
            setCookie('preauthStatementUrlExpiresAt', statementUrlExpiresAt);
          } else {
            yield call(AsyncStorage.setItem, 'preauthStatementUrlExpiresAt', statementUrlExpiresAt);
          }
        }
        delayTime = 300000; // 5 min in ms
      }
    }
  } catch {
    yield put(setPreAuthFeeShedules({ feeStatements: [] }));
  }
}

export function* totpSaga({ baseUrl, navigationObj }, { payload: { passcode } }) {
  try {
    const endpoint = `${baseUrl}/login/totp`;
    const { requestData } = yield race({
      cancel: take(TOTP_CANCEL_REQUEST),
      requestData: call(post, endpoint, { passcode }, { sendDeviceTokens: true }),
    });
    if (requestData) {
      const sessionState = yield call(getAuthStatus);
      yield call(postAuthRequestSaga, {
        baseUrl,
        navigationObj,
        requestData,
        sessionState,
        successAction: totpRequestSuccess,
      });
    }
  } catch (error) {
    const { additionalData } = error;
    const { errorCode, errorDescription } = additionalData || {};
    if (errorCode === 1002 && errorDescription) {
      yield put(totpRequestFailure(errorDescription));
    } else {
      yield put(totpRequestFailure(ERROR_MESSAGES[errorCode] || GENERAL_SERVER_ERROR_MSG));
    }
  }
}

export function* postAuthRequestSaga({
  baseUrl,
  isInitRequest,
  isLoginRequest,
  navigationObj,
  requestData = {},
  sessionState,
  skipDelayCheckmark = false,
  successAction,
}) {
  const {
    chatbotAuthToken,
    clientInfo = {},
    // encryptedClients,
    goldMigrationPLSession = {},
    goldMigrationPLSessionError = false,
    loanDetails = {},
    loans = [],
    primaryLoanPaperlessStatus,
    profile = {},
    refreshToken = '',
    sameClientCodes = false,
    selectedLoanLanguagePreference,
    ssoLogoutUrl,
    testFlags = {},
    treatments,
  } = requestData;
  const { currentSetting: { username = '' } = {} } = profile;
  const isMobile = yield select(makeSelectMobile('isMobile'));
  const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));

  if (sessionState === AUTH_FULL) {
    if (isNativeMobile) {
      if (
        clientInfo.clientAuthentication.grantType === 'sso_oidc'
        && profile.currentSetting.authenticatedBy === 'SSO'
      ) {
        // yield call(setEncryptedClients, encryptedClients);
      } else {
        const { isNewUser, usernameKeychain } = yield call(checkIfIsNewUser, username);
        if (isNewUser && usernameKeychain !== INVALID_USERNAME_KEYCHAIN) {
          yield call(KeychainStore.deleteAll);
          yield put(disableAltAuthMethods());
        }
        // check for corrupt biometric enrollment/pin and reset if necessary
        const biometricsKeychain = yield call(KeychainStore.getBiometricsEnrollment);
        const resetBiometrics = biometricsKeychain === INVALID_BIOMETRICS_ENROLLMENT_KEYCHAIN;
        const pinKeychain = yield call(KeychainStore.getPin);
        const resetPin = pinKeychain === INVALID_PIN_KEYCHAIN;
        if (resetBiometrics || resetPin) {
          const supportedBiometricAuthenticationTypes =
            yield call(Biometrics.supportedAuthenticationTypes);
          if (resetBiometrics && resetPin) {
            yield call(KeychainStore.deleteBiometrics);
            yield call(KeychainStore.deletePin);
            yield put(updateBiometricsAndPinInfo({
              isBiometricLoginEnabled: false,
              isPinEnabled: false,
              supportedBiometricAuthenticationTypes,
            }));
          } else if (resetBiometrics) {
            yield call(KeychainStore.deleteBiometrics);
            yield put(updateBiometricsAndPinInfo({
              isBiometricLoginEnabled: false,
              isPinEnabled: pinKeychain !== null,
              supportedBiometricAuthenticationTypes,
            }));
          } else {
            yield call(KeychainStore.deletePin);
            yield put(updateBiometricsAndPinInfo({
              isBiometricLoginEnabled: biometricsKeychain !== null,
              isPinEnabled: false,
              supportedBiometricAuthenticationTypes,
            }));
          }
        }
        yield call(KeychainStore.setUsernameAndRefreshToken, refreshToken, username);
      }
      yield call(removePinAttemptCount);
      yield call(removeLockoutExpirationTime);
    }
    const { [SELECTED_LOAN_SESSION_KEY]: selectedLoan } = yield call(retrieveSession);
    const { clientName } = clientInfo;
    const { isMurabaha } = loanDetails.summary ? loanDetails.summary : {};
    const { id: userId = null } = yield call(getAuthProp, 'user') || {};
    if (window.gtag) {
      window.gtag('set', 'user_properties', {
        clientName,
        maskedLoanNumber: selectedLoan,
        user_id: userId,
      });
    }
    // When language picker is enabled, override account language unless set to 'default' option
    const accountLanguage = isMurabaha ? LANGUAGE_ENGLISH_MURABAHA : LANGUAGE_ENGLISH;
    if (testFlags.showLanguagePicker) {
      const pickerLanguage = yield call(AsyncStorage.getItem, STORAGE_KEY_LANGUAGE_PICKER_VALUE);
      yield put(setSelectedLanguage(pickerLanguage || accountLanguage));
    } else {
      yield put(setSelectedLanguage(accountLanguage));
    }
    yield put(updateUserLoansData({
      clientInfo,
      loanDetails,
      loans,
      primaryLoanPaperlessStatus,
      profile,
      sameClientCodes,
      selectedLoan,
      selectedLoanLanguagePreference,
      ssoLogoutUrl,
      testFlags,
      treatments,
    }));
    yield put(setChatbotAuthToken(chatbotAuthToken));

    const dismissedDocUploadStatusBanners = yield call(getDismissedDocUploadStatusBanners);
    if (dismissedDocUploadStatusBanners.length) {
      yield put(updateDismissedDocUploadStatusBanners(dismissedDocUploadStatusBanners));
    }

    if (isInitRequest) {
      yield put(successAction({ isLoggedIn: true }));
    } else {
      yield put(successAction());
      if (!skipDelayCheckmark) {
        yield delay(CHECKMARK_TIME_UNTIL_TRANSITION);
      }
      yield put(updateIsLoggedIn({ isLoggedIn: true }));
    }
    yield put(updatePaymentHomeInit(true));
    yield put(refreshMonitor());
  } else {
    const { feeStatements = [], preAuthNotifications = {} } = requestData;
    const { isMurabaha } = clientInfo;
    const dismissedPreloginBanners = yield call(getDismissedPreLoginBanners);
    if (dismissedPreloginBanners.length) {
      yield put(updateDismissedBanners(dismissedPreloginBanners));
    }
    yield put(updateGoldMigrationSession({
      goldMigrationPLSession,
      goldMigrationPLSessionError,
    }));
    // When language picker is enabled, override account language unless set to 'default' option
    const accountLanguage = isMurabaha ? LANGUAGE_ENGLISH_MURABAHA : LANGUAGE_ENGLISH;
    if (testFlags.showLanguagePicker) {
      const pickerLanguage = yield call(AsyncStorage.getItem, STORAGE_KEY_LANGUAGE_PICKER_VALUE);
      yield put(setSelectedLanguage(pickerLanguage || accountLanguage));
    } else {
      yield put(setSelectedLanguage(accountLanguage));
    }
    yield put(updateUserLoansData({
      clientInfo,
      loans,
      profile,
      sameClientCodes,
      testFlags,
      treatments,
    }));
    if (!isEmpty(preAuthNotifications)) {
      yield put(setPreAuthNotifications(preAuthNotifications));
    }
    yield put(setPreAuthFeeShedules({ feeStatements }));
    if (!isEmpty(feeStatements)) {
      const { statementUrlExpiresAt } = feeStatements[0];
      if (!isNativeMobile) {
        setCookie('preauthStatementUrlExpiresAt', statementUrlExpiresAt);
      } else {
        yield call(AsyncStorage.setItem, 'preauthStatementUrlExpiresAt', statementUrlExpiresAt);
      }
    }
    yield put(successAction());
    const isSso = yield select(getIsSso());
    const isSslAuth = yield call(getAuthProp, 'isSslAuth');
    const ssoType = yield select(getSsoType());
    const navigationHandler = getNavHandler({ isLoginRequest, isMobile, navigationObj });
    if (!isInitRequest) {
      yield delay(CHECKMARK_TIME_UNTIL_TRANSITION);
      const path = REDIRECT_NAV_DICT[isMobile ? 'mobile' : 'desktop'][sessionState];
      if (path) {
        navigationHandler(path);
      }
    } else if (isSso || isSslAuth) {
      // This path means there was an SSL SSO error
      if (!isNativeMobile && window.location.pathname === '/login/ssl') return;
      // This path is for desktop and mobile web OIDC
      if (!isNativeMobile && window.location.pathname === '/oauthcallback') return;

      const isSsoError = isSso && window.location.pathname === '/login/sso'
        && (!!getQueryParam('error') || !!getQueryParam('redirect'));
      if (ssoType === 'samlIDP' && isSsoError) return;
      if (ssoType === 'samlIDP' || ssoType === 'ssl') {
        if (isMobile) {
          const screenName = ssoType === 'ssl' ? 'LoginSslError' : 'LoginSso';
          /**
           * Need to give NavigationContainer time to mount and apply "ref={navigationRef}" so that
           * the navigationHandler gets instantiated. Otherwise we'll call "dispatch" on undefined
           * and an error will be thrown and the StackNavigator will just stick to showing its
           * "initialRouteName". (Admittedly just delaying here is a bit imprecise and
           * hacky, but this solution seems to work consistently and this edge case is
           * relatively obscure/infrequent and so doesn't warrant extensive research for a better
           * solution.)
           */
          yield delay(1000);
          navigationHandler(screenName);
        } else {
          const subPath = ssoType === 'ssl' ? 'ssl' : 'sso';
          navigationHandler(`/login/${subPath}?error=49`);
        }
        return;
      }

      /**
       * TODO: Refactor out all references to "isSslAuth" (after comfirming that's safe).
       *
       * All "isSslAuth" references seem to be legacy, likely while we were initially
       * developing the ssl flow but before the Silver API's "clientAuthentication" had been
       * updated to provide any ssl data. If I'm tracking the code correctly, "isSslAuth" will
       * always be false at all points of the app flow. (Almost) everywhere we check for isSslAuth
       * we also check for (isSslAuth || isSso), and now 'isSso' is true for the ssl flow.
       *
       * The only two apparent exceptions are the following, both of which are minor edge cases:
       *  - packages/mobile/app/components/NavigationDrawer/NavMenu/index.js
       *  - and the `selectShowLogoutButton` selector
       */
      if (isSslAuth) {
        yield put(setIsSslAuth({ isSslAuth }));
      }

      if (!isMobile && !isSsoError) {
        const path = isSso ? '/login/sso' : '/login';
        navigationHandler(path);
      }
    }
    yield call(refreshPreauthStatementUrlSaga, baseUrl);
  }
}

export function* fetchNativeClientsSaga({ baseUrl }) {
  try {
    const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
    if (isNativeMobile) {
      const encryptedClients = yield call(getEncryptedClients);
      if (encryptedClients) {
        const { clients } = yield call(
          putRequest,
          `${baseUrl}/login/clients`,
          { clients: encryptedClients },
        );
        yield put(setNativeClientList({ clients }));
      }
    }
  } catch ({ additionalData }) {
    const { errorDescription } = additionalData || {};
    if (errorDescription === 'decryption_error') {
      yield call(deleteEncryptedClients);
      yield put(nativeClientsRequestFailure(NATIVE_CLIENT_RESET_ERROR_MESSAGE));
    } else {
      yield put(nativeClientsRequestFailure(NATIVE_CLIENT_ERROR_MESSAGE));
    }
    throw new Error('nativeClientsFetchError');
  }
}

export function* linkClientSaga({ baseUrl, navigationObj }, { payload }) {
  try {
    const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
    if (isNativeMobile) {
      const isLockedOut = yield call(getIsLockedOut, 'clientLink');
      if (isLockedOut) {
        yield put(linkNativeClientFailure({ bannerError: LINK_CLIENT_LOCKOUT_MESSAGE }));
        return;
      }
      yield call(removeLockoutExpirationTime, 'clientLink');
      const endpoint = `${baseUrl}/login/clients`;
      const prevEncryptedClients = yield call(getEncryptedClients);
      const {
        clients,
        encryptedClients,
        error,
      } = yield call(post, endpoint, { ...payload, clients: prevEncryptedClients });
      if (error) {
        yield call(handleClientLinkFailure);
        yield put(linkNativeClientFailure({ bannerError: REGISTER_STEP_ONE_ERROR }));
        return;
      }
      yield call(removeClientLinkAttemptCount);
      yield call(setEncryptedClients, encryptedClients);
      yield put(linkNativeClientSuccess());
      yield delay(CHECKMARK_TIME_UNTIL_TRANSITION);
      yield put(setNativeClientList({ clients }));
      const navigationHandler = getNavHandler({ isMobile: true, navigationObj });
      navigationHandler('PreAuthNavigator', { screen: 'ClientSelect' });
    }
  } catch ({ additionalData }) {
    const { errorDescription } = additionalData || {};
    if (errorDescription === 'decryption_error') {
      yield call(deleteEncryptedClients);
      yield put(linkNativeClientFailure({ serviceError: NATIVE_CLIENT_RESET_ERROR_MESSAGE }));
    } else {
      yield put(linkNativeClientFailure({ bannerError: DEFAULT_ERROR_MSG }));
    }
  }
}

// eslint-disable-next-line no-empty-pattern
export function* setLanguagePickerSaga({}, { payload: { language } }) {
  try {
    const { showLanguagePicker } = yield select(makeSelectMain('testFlags'));
    const isLoggedIn = yield select(makeSelectMain('isLoggedIn'));

    if (showLanguagePicker) {
      yield call(AsyncStorage.setItem, STORAGE_KEY_LANGUAGE_PICKER_VALUE, language);
      // Set language to picker value, or default to account language if none provided
      if (language) {
        yield put(setSelectedLanguage(language));
      } else {
        let isMurabaha;
        if (isLoggedIn) {
          ({ summary: { isMurabaha } } = yield select(makeSelectMain('loanDetails')));
        } else {
          ({ isMurabaha } = yield select(makeSelectMain('clientInfo')));
        }
        yield put(setSelectedLanguage(isMurabaha
          ? LANGUAGE_ENGLISH_MURABAHA
          : LANGUAGE_ENGLISH));
      }
    }
  } catch {
    // No need to handle errors as language picker is used for testing purposes only
  }
}

export default (options) => function* rootSaga() {
  yield all([
    takeEvery('*', logActionSaga),
    takeLatest(BIOMETRIC_LOGIN_REQUEST, biometricLoginRequestSaga),
    takeLatest(CHANGE_SELECTED_LOAN, changeSelectedLoanSaga, options),
    takeLeading(FETCH_RESPA_LETTER, fetchRespaLetterSaga, options),
    takeLatest(FETCH_TRANSFER_STATUS, fetchTransferStatusSaga, options),
    takeLatest([GOLD_MIGRATION_LOGIN_REQUEST], goldMigrationLoginSaga, options),
    takeLatest(INITIALIZE_REQUEST, initializeRequestSaga, options),
    takeLatest(LINK_NATIVE_CLIENT, linkClientSaga, options),
    takeLatest([LOGIN_REQUEST, CANCEL_LOGIN], loginSaga, options),
    takeLatest(LOGOUT_REQUEST, logoutRequestSaga, options),
    takeLatest(MFA_OPTION_REQUEST, mfaSelectionSaga, options),
    takeLatest(OAUTH_LOGIN, oauthLoginSaga, options),
    takeLatest(FETCH_OIDC_CONFIG, fetchOidcConfigSaga),
    takeLatest(OPEN_PIN_DRAWER, openPinDrawerSaga),
    takeLatest(PIN_LOGIN_REQUEST, pinLoginRequestSaga),
    takeLatest(RECONSENT_REQUEST, reconsentSaga, options),
    takeLatest(REFRESH_MONITOR, refreshMonitorSaga, options),
    takeLatest(REFRESH_TOKEN, refreshTokenSaga, options),
    takeLatest(SET_LANGUAGE_PICKER, setLanguagePickerSaga, options),
    takeLatest(TOTP_REQUEST, totpSaga, options),
    takeLeading(PUT_LANGUAGE_PREFERENCE, putNYCLanguagePreferenceSaga, options),
    takeLeading(PUT_PAPERLESS_SETTING, putPaperlessSettingSaga, options),
  ]);
};
